import { CompanyId, getToken, UUID, ParkingId, Role, GroupPermission, ListOptions } from "helpers";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { environment } from "environment";
import { RolesWithDetailsResponse } from "./apiHelpers";

// Define a service using a base URL and expected endpoints
export const roleApi = createApi({
  reducerPath: "roleApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${environment.dev.baseUrl}/auth/role`,
    prepareHeaders: (headers) => {
      headers.set("authorization-token", getToken() || "");
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getRole: builder.query<Role, GetRoleArgs>({
      query: ({ company_id, id }) => ({
        url: `${company_id}/${id}`,
      }),
      transformResponse: (result: any) => result.data,
      transformErrorResponse: (error: any) => error.data.apierror,
    }),

    getRolesWithDetails: builder.query<RolesWithDetailsResponse, ListOptions>({
      query: ({
        company_id,
        page = 0,
        pageSize = 50,
        search,
        order,
        sort,
        permission_level_id,
        include_default_permissions = true,
      }) => ({
        url: `with-details/list/${company_id}`,
        params: {
          page: page + 1,
          pageSize,
          search: search ? search.replace(/ /g, "-") : undefined,
          sort,
          order,
          permission_level_id,
          include_default_permissions,
        },
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),

    createRole: builder.mutation<Role, CreateRoleArgs>({
      query: ({ company_id, role, permissions, is_predefined = false }) => ({
        url: "create",
        method: "PUT",
        body: {
          company_id,
          role,
          permissions,
          is_predefined,
        },
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),

    updateRole: builder.mutation<Role, UpdateRoleArgs>({
      query: ({ role_id, permissions }) => ({
        url: "update",
        method: "POST",
        body: {
          role_id,
          permissions,
        },
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),

    attachRole: builder.mutation<boolean, AttachRoleArgs>({
      query: ({ username, roles }) => ({
        url: "attach",
        method: "POST",
        body: {
          username,
          roles,
        },
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),

    // TODO: decide payload with BE !
    detachRole: builder.mutation<boolean, DetachRoleArgs>({
      query: ({ users }) => ({
        url: "detach",
        method: "POST",
        body: {
          users,
        },
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),

    deleteRole: builder.mutation<boolean, DeleteRoleArgs>({
      query: ({ company_id, id }) => ({
        url: `${company_id}/${id}`,
        method: "DELETE",
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),
  }),
});

// Export hooks for usage in functional components, which are auto-generated based on the defined endpoints
export const {
  useGetRoleQuery,
  useLazyGetRoleQuery,
  useGetRolesWithDetailsQuery,
  useLazyGetRolesWithDetailsQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useAttachRoleMutation,
  useDetachRoleMutation,
  useDeleteRoleMutation,
  usePrefetch: useRoleApiPrefetch,
} = roleApi;

interface GetRoleArgs {
  company_id: CompanyId;
  id: number;
}

interface CreateRoleArgs {
  company_id: CompanyId;
  role: string;
  permissions: GroupPermission[];
  is_predefined?: boolean;
}

interface UpdateRoleArgs {
  role_id: number;
  permissions: GroupPermission[];
}

interface RoleParking {
  role_id: number;
  parking_id?: ParkingId;
}

interface AttachRoleArgs {
  username: UUID;
  roles: RoleParking[];
}

type DetachUserRole = RoleParking & {
  username: UUID;
};

// TODO: decide payload with BE !
type DetachRoleArgs = {
  users: DetachUserRole[];
};

type DeleteRoleArgs = GetRoleArgs;
